import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Eye, PhoneForwarded, X, History } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const LNP_STATUSES = ["Submitted", "FOC Received", "Scheduled", "Completed", "Rejected", "Cancelled"];
const TONE = {
  "Submitted": "bg-blue-100 text-blue-700 border-blue-200",
  "FOC Received": "bg-violet-100 text-violet-700 border-violet-200",
  "Scheduled": "bg-amber-100 text-amber-700 border-amber-200",
  "Completed": "bg-green-100 text-green-700 border-green-200",
  "Rejected": "bg-rose-100 text-rose-700 border-rose-200",
  "Cancelled": "bg-slate-100 text-slate-500 border-slate-200",
};
const fmtDate = (s) => (s ? new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDT = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const emptyNumber = () => ({ number: "", carrier: "", carrier_ref: "", status: "Submitted", note: "" });

const StatusBadge = ({ status }) => <Badge variant="outline" className={TONE[status] || ""}>{status}</Badge>;

const OrderDialog = ({ projects, order, readOnly, lockedProjectId, onClose, onSaved }) => {
  const editing = !!order;
  const [form, setForm] = useState(order || { project_id: lockedProjectId || "", carrier_reference: "", date_submitted: "", due_date: "", note: "", numbers: [emptyNumber()] });
  const [busy, setBusy] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: order?.status || "Submitted", note: "" });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (i, k, v) => setForm((f) => ({ ...f, numbers: f.numbers.map((n, idx) => (idx === i ? { ...n, [k]: v } : n)) }));
  const addNum = () => setForm((f) => ({ ...f, numbers: [...f.numbers, emptyNumber()] }));
  const rmNum = (i) => setForm((f) => ({ ...f, numbers: f.numbers.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!editing && !form.project_id) { toast.error("Pick a project"); return; }
    const nums = (form.numbers || []).filter((n) => (n.number || "").trim());
    if (nums.length === 0) { toast.error("Add at least one number"); return; }
    setBusy(true);
    const body = { project_id: form.project_id, carrier_reference: form.carrier_reference, date_submitted: form.date_submitted || null, due_date: form.due_date || null, note: form.note, numbers: nums };
    try {
      const { data } = editing ? await api.put(`/lnp/${order.port_order_id}`, body) : await api.post("/lnp", body);
      toast.success(editing ? "Port order updated" : "Port order created");
      onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };
  const addStatus = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lnp/${order.port_order_id}/status`, statusForm);
      toast.success("Status updated");
      setStatusForm({ status: data.status, note: "" });
      setForm(data); onSaved(data, true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneForwarded className="h-5 w-5 text-primary" /> {editing ? `Port order — ${order.project_name}` : "New port order"}{editing && <StatusBadge status={form.status} />}</DialogTitle>
          <DialogDescription>{readOnly ? "Read-only view of this port order." : "Track numbers being ported, carrier references and due dates. Each number can have its own carrier & status."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!editing && !lockedProjectId && (
            <div className="space-y-1.5">
              <Label>Project *</Label>
              <Select value={form.project_id} onValueChange={(v) => setField("project_id", v)}>
                <SelectTrigger data-testid="lnp-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Carrier reference</Label><Input data-testid="lnp-carrier-ref" disabled={readOnly} value={form.carrier_reference || ""} onChange={(e) => setField("carrier_reference", e.target.value)} placeholder="PON / order #" /></div>
            <div className="space-y-1.5"><Label>Date submitted</Label><Input type="date" data-testid="lnp-date-submitted" disabled={readOnly} value={form.date_submitted ? form.date_submitted.slice(0, 10) : ""} onChange={(e) => setField("date_submitted", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Due / FOC date</Label><Input type="date" data-testid="lnp-due-date" disabled={readOnly} value={form.due_date ? form.due_date.slice(0, 10) : ""} onChange={(e) => setField("due_date", e.target.value)} /></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Numbers being ported</Label>{!readOnly && <Button size="sm" variant="outline" onClick={addNum} data-testid="lnp-add-number"><Plus className="h-3.5 w-3.5" /> Add number</Button>}</div>
            <div className="space-y-2">
              {(form.numbers || []).map((n, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`lnp-number-${i}`}>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-12">
                    <Input className="sm:col-span-4" disabled={readOnly} value={n.number} onChange={(e) => setNum(i, "number", e.target.value)} placeholder="+1 403 555 0100" data-testid={`lnp-num-number-${i}`} />
                    <Input className="sm:col-span-3" disabled={readOnly} value={n.carrier} onChange={(e) => setNum(i, "carrier", e.target.value)} placeholder="Losing carrier" data-testid={`lnp-num-carrier-${i}`} />
                    <Input className="sm:col-span-2" disabled={readOnly} value={n.carrier_ref} onChange={(e) => setNum(i, "carrier_ref", e.target.value)} placeholder="Ref #" data-testid={`lnp-num-ref-${i}`} />
                    <div className="sm:col-span-3 flex items-center gap-1.5">
                      <Select value={n.status} onValueChange={(v) => setNum(i, "status", v)} disabled={readOnly}>
                        <SelectTrigger className="h-9" data-testid={`lnp-num-status-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{LNP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      {!readOnly && form.numbers.length > 1 && <button onClick={() => rmNum(i)} className="text-slate-300 hover:text-red-500" data-testid={`lnp-rm-number-${i}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </div>
                  <Input className="mt-2 h-8 text-xs" disabled={readOnly} value={n.note} onChange={(e) => setNum(i, "note", e.target.value)} placeholder="Note for this number (optional)" data-testid={`lnp-num-note-${i}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5"><Label>Order note</Label><Textarea rows={2} disabled={readOnly} value={form.note || ""} onChange={(e) => setField("note", e.target.value)} data-testid="lnp-note" placeholder="General notes for the porting team…" /></div>

          {editing && (
            <div className="rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2"><History className="h-4 w-4 text-primary" /><span className="text-sm font-semibold text-slate-700">Status updates</span></div>
              <div className="max-h-48 space-y-2 overflow-y-auto px-3 py-2" data-testid="lnp-status-log">
                {(form.status_log || []).slice().reverse().map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <StatusBadge status={s.status} />
                    <div className="min-w-0"><div className="text-slate-500">{s.by_name} · {fmtDT(s.at)}</div>{s.note && <div className="text-slate-700">{s.note}</div>}</div>
                  </div>
                ))}
              </div>
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3 py-2">
                  <Select value={statusForm.status} onValueChange={(v) => setStatusForm((s) => ({ ...s, status: v }))}>
                    <SelectTrigger className="h-9 w-[160px]" data-testid="lnp-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LNP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="h-9 flex-1" value={statusForm.note} onChange={(e) => setStatusForm((s) => ({ ...s, note: e.target.value }))} placeholder="Add a note (optional)" data-testid="lnp-status-note" />
                  <Button size="sm" onClick={addStatus} disabled={busy} data-testid="lnp-status-add" className="bg-primary hover:bg-primary/90 text-primary-foreground">Update</Button>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!readOnly && <Button onClick={save} disabled={busy} data-testid="lnp-save" className="bg-primary hover:bg-primary/90 text-primary-foreground">{busy ? "Saving…" : editing ? "Save changes" : "Create port order"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LnpBoard = ({ projects = [], canEdit = false, projectId = null, section = false, onChanged }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { order, readOnly } | { create: true }
  const [delTarget, setDelTarget] = useState(null);
  const load = () => { setLoading(true); api.get(projectId ? `/lnp?project_id=${projectId}` : "/lnp").then((r) => setOrders(r.data || [])).catch(() => setOrders([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const onSaved = (doc, keepOpen) => {
    setOrders((arr) => { const exists = arr.some((o) => o.port_order_id === doc.port_order_id); return exists ? arr.map((o) => (o.port_order_id === doc.port_order_id ? doc : o)) : [doc, ...arr]; });
    onChanged?.();
    if (!keepOpen) setDialog(null);
  };
  const del = async (oid) => {
    try { await api.delete(`/lnp/${oid}`); toast.success("Port order deleted"); setDelTarget(null); setOrders((arr) => arr.filter((o) => o.port_order_id !== oid)); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const tableEl = loading ? <p className={section ? "px-5 py-4 text-sm text-slate-400" : "p-5 text-sm text-slate-400"}>Loading…</p> : orders.length === 0 ? (
    <p className={section ? "px-5 py-4 text-sm text-slate-400" : "p-5 text-sm text-slate-400"}>No port orders yet.{canEdit ? " Create one to start tracking a port." : ""}</p>
  ) : (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
          {!projectId && <th className="px-4 py-3">Project</th>}<th className="px-4 py-3">Numbers</th><th className="px-4 py-3">Carrier ref</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Due / FOC</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
        </tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.port_order_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`lnp-row-${o.port_order_id}`}>
              {!projectId && <td className="px-4 py-3"><div className="font-medium text-slate-800">{o.project_name}</div>{o.partner_name && <div className="text-xs text-slate-400">{o.partner_name}</div>}</td>}
              <td className="px-4 py-3 text-slate-600">{(o.numbers || []).length}</td>
              <td className="px-4 py-3 text-slate-600">{o.carrier_reference || "—"}</td>
              <td className="px-4 py-3 text-slate-600">{fmtDate(o.date_submitted)}</td>
              <td className="px-4 py-3 text-slate-600">{fmtDate(o.due_date)}</td>
              <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  {canEdit ? (
                    <>
                      <button onClick={() => setDialog({ order: o })} title="Edit" className="text-slate-400 hover:text-primary" data-testid={`lnp-edit-${o.port_order_id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDelTarget(o)} title="Delete" className="text-slate-300 hover:text-red-500" data-testid={`lnp-del-${o.port_order_id}`}><Trash2 className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <button onClick={() => setDialog({ order: o, readOnly: true })} title="View" className="text-slate-400 hover:text-primary" data-testid={`lnp-view-${o.port_order_id}`}><Eye className="h-4 w-4" /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const dialogsEl = (
    <>
      {dialog && <OrderDialog projects={projects} order={dialog.order} readOnly={dialog.readOnly} lockedProjectId={projectId} onClose={() => setDialog(null)} onSaved={onSaved} />}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete port order?</DialogTitle><DialogDescription>This removes the port order for {delTarget?.project_name} and its status history. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button><Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => del(delTarget.port_order_id)} data-testid="lnp-del-confirm">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (section) {
    const today = new Date().toISOString().slice(0, 10);
    const isOpenOrder = (o) => !["Completed", "Cancelled"].includes(o.status);
    const openOrders = orders.filter(isOpenOrder);
    const overdue = openOrders.filter((o) => o.due_date && o.due_date.slice(0, 10) < today);
    const countTone = overdue.length ? "rose" : openOrders.length ? "amber" : "green";
    return (
      <CollapsibleCard
        testid="lnp-board"
        icon={PhoneForwarded}
        title="LNP — Number Porting"
        persistKey={`proj-${projectId}-lnp`}
        defaultOpen={false}
        count={orders.length}
        countTone={countTone}
        headerInfo={overdue.length ? <span className="text-xs font-medium text-rose-600" data-testid="lnp-overdue-info">{overdue.length} overdue</span> : null}
        headerRight={canEdit ? <Button size="sm" variant="outline" onClick={() => setDialog({ create: true })} data-testid="lnp-new-order"><Plus className="h-4 w-4" /> New port order</Button> : null}
      >
        {tableEl}
        {dialogsEl}
      </CollapsibleCard>
    );
  }

  return (
    <div data-testid="lnp-board">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800"><PhoneForwarded className="h-5 w-5 text-primary" /> LNP — Number Porting</h1>
          <p className="text-sm text-slate-400">Port orders handled by the porting team, across all projects. Quick reference for numbers, carriers, dates and status.</p>
        </div>
        {canEdit && <Button onClick={() => setDialog({ create: true })} data-testid="lnp-new-order" className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> New port order</Button>}
      </div>
      <Card className="border-slate-200">{tableEl}</Card>
      {dialogsEl}
    </div>
  );
};
